import Backend from 'i18next-xhr-backend';
import { logger } from "src/logger";

// The i18next-xhr-backend request function
type Ajax = (url: string, options: object, callback: (response: string, xhr: XMLHttpRequest) => void,
             data: object | string, cache: boolean) => void;

// Load path for a locale that has no translation file yet
export const NO_TRANSLATION_FILE = 'no-translation-file';

/**
 * Resolves the path of a locale's translation file.
 *
 * The Katal language map only has entries for locales with a PUFF-J file in /translations, so a locale enabled
 * before Panther has delivered its file gets {@link NO_TRANSLATION_FILE} rather than "/undefined".
 *
 * @param languageMap the Katal map of locale to translation file path.
 * @param language the requested locale (e.g. hi-IN).
 * @return the translation file path, or {@link NO_TRANSLATION_FILE}.
 */
export const getTranslationLoadPath = (languageMap: Record<string, string>, language: string): string => {
    if (languageMap[language]) {
        return `/${languageMap[language]}`;
    }

    logger.warn(`No translation file for ${language}; showing the default language`);
    return NO_TRANSLATION_FILE;
}

/**
 * Wraps the backend's request function so a locale with no translation file loads an empty bundle.
 *
 * An empty bundle sends every lookup on to fallbackLng, so the page shows the default language's strings
 * with the default language's plural rules. Loading the default file under the new locale instead would apply
 * that locale's plural rules to English strings (hi-IN and pt-BR treat 0 as singular). A failed request would
 * also fall back, but reports i18n initialization as failed.
 *
 * @param ajax the backend's request function.
 * @return a request function that answers {@link NO_TRANSLATION_FILE} with an empty bundle.
 */
export const withEmptyBundleForMissingFiles = (ajax: Ajax): Ajax => {
    return (url, options, callback, data, cache) => {
        if (url === NO_TRANSLATION_FILE) {
            callback('{}', { status: 200 } as XMLHttpRequest);
        } else {
            ajax(url, options, callback, data, cache);
        }
    };
}

/**
 * Builds the i18next-xhr-backend options for the Katal language map.
 *
 * @param languageMap the Katal map of locale to translation file path.
 * @return the backend options.
 */
export const getBackendOptions = (languageMap: Record<string, string>) => {
    return {
        loadPath: (languages: string[]) => getTranslationLoadPath(languageMap, languages[0]),
        // Wraps the backend's default request function
        ajax: withEmptyBundleForMissingFiles(new Backend().options.ajax!),
        crossDomain: true,
    };
}
